import React, { useState, useEffect } from 'react';
import { Table, Card, Btn, Modal, Input, Select, Textarea, FormGrid, SectionHeader, Badge, StatCard, NeedProject } from '../components/UI';
import { fmt, fmtNum, fmtDate, statusColor, exportCSV } from '../utils';
import * as api from '../api';
import { useParams } from 'react-router-dom';

// ─── VARIATIONS ──────────────────────────────
function VariationsTab({ projectId, toast }) {
  const [rows, setRows]       = useState([]);
  const [boq, setBOQ]         = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(false);
  const [detail, setDetail]   = useState(null);
  const [form, setForm]       = useState({ quantity_variations: [{}] });
  const [saving, setSaving]   = useState(false);

  const load = async () => {
    setLoading(true);
    try { const [v, b] = await Promise.all([api.listVariations(projectId), api.listBOQ(projectId)]); setRows(Array.isArray(v)?v:[]); setBOQ(Array.isArray(b)?b:[]); }
    catch {} setLoading(false);
  };
  useEffect(() => { load(); }, [projectId]);

  const totalApproved = rows.filter(r=>r.status==='approved').reduce((s,r)=>s+parseFloat(r.approved_cost||r.estimated_cost||0),0);

  return (
    <>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
        <div style={{fontSize:13,color:'var(--text-muted)'}}>Total Approved: <strong style={{fontFamily:'var(--mono)',color:'var(--green)'}}>{fmt(totalApproved)}</strong></div>
        <div style={{display:'flex',gap:8}}>
          <Btn variant="ghost" onClick={()=>exportCSV(rows,'variations')}>⬇ CSV</Btn>
          <Btn onClick={()=>{setForm({variation_type:'addition',quantity_variations:[{}]});setModal(true);}}>+ New VO</Btn>
        </div>
      </div>
      <Card>
        <Table loading={loading} cols={[
          {key:'vo_number',      label:'VO #',       render:v=><span style={{fontFamily:'var(--mono)',color:'var(--purple)'}}>{v}</span>},
          {key:'title',          label:'Title'},
          {key:'variation_type', label:'Type',       render:v=><Badge color={statusColor(v)}>{v}</Badge>},
          {key:'status',         label:'Status',     render:v=><Badge color={statusColor(v)}>{v}</Badge>},
          {key:'submitted_date', label:'Submitted',  render:v=>fmtDate(v)},
          {key:'estimated_cost', label:'Est. Cost',  render:v=><span style={{fontFamily:'var(--mono)'}}>{fmt(v)}</span>},
          {key:'approved_cost',  label:'Approved',   render:v=>v?<span style={{fontFamily:'var(--mono)',color:'var(--green)',fontWeight:700}}>{fmt(v)}</span>:'—'},
          {key:'variation_id',   label:'',           render:(id,row)=>(
            <div style={{display:'flex',gap:6}}>
              <Btn variant="ghost"   size="sm" style={{padding:'3px 8px'}} onClick={async()=>{try{const d=await api.getVariation(id);setDetail(d);}catch(e){toast.show(e.message,'error');}}}>View</Btn>
              {row.status==='pending'&&<>
                <Btn variant="success" size="sm" style={{padding:'3px 8px'}} onClick={async()=>{try{await api.approveRejectVariation(id,{status:'approved',approved_cost:row.estimated_cost,approved_date:new Date().toISOString().slice(0,10)});toast.show('Approved');load();}catch(e){toast.show(e.message,'error');}}}>✓</Btn>
                <Btn variant="danger"  size="sm" style={{padding:'3px 8px'}} onClick={async()=>{const r=window.prompt('Reason?');if(r)try{await api.approveRejectVariation(id,{status:'rejected',rejection_reason:r});toast.show('Rejected');load();}catch(e){toast.show(e.message,'error');}}}>✕</Btn>
              </>}
            </div>
          )},
        ]} rows={rows} />
      </Card>

      {detail && (
        <Modal title={`VO — ${detail.vo_number}`} onClose={()=>setDetail(null)} width={700}>
          <div style={{marginBottom:12,display:'flex',gap:10,flexWrap:'wrap'}}>
            <Badge color={statusColor(detail.variation_type)}>{detail.variation_type}</Badge>
            <Badge color={statusColor(detail.status)}>{detail.status}</Badge>
            <span style={{fontSize:12,color:'var(--text-muted)'}}>{detail.description}</span>
          </div>
          <Table cols={[
            {key:'boq_item_id',      label:'BOQ Item',     render:v=>{const b=boq.find(x=>x.boq_item_id===v);return b?<span style={{fontFamily:'var(--mono)',fontSize:11}}>{b.boq_code}</span>:'—';}},
            {key:'original_quantity',label:'Orig Qty',     render:v=>fmtNum(v)},
            {key:'new_quantity',     label:'New Qty',      render:v=>fmtNum(v)},
            {key:'rate',             label:'Rate',         render:v=>fmt(v)},
            {key:'cost_impact',      label:'Cost Impact',  render:v=><span style={{fontFamily:'var(--mono)',color:parseFloat(v)>=0?'var(--amber)':'var(--green)',fontWeight:700}}>{fmt(v)}</span>},
            {key:'reason',           label:'Reason'},
          ]} rows={detail.quantity_variations||[]} />
        </Modal>
      )}

      {modal && (
        <Modal title="New Variation Order" onClose={()=>setModal(false)} width={740}>
          <div style={{display:'flex',flexDirection:'column',gap:14}}>
            <FormGrid>
              <Input label="VO Number" required value={form.vo_number||''} onChange={v=>setForm(f=>({...f,vo_number:v}))} />
              <Input label="Title" required value={form.title||''} onChange={v=>setForm(f=>({...f,title:v}))} />
              <Select label="Type" value={form.variation_type||'addition'} onChange={v=>setForm(f=>({...f,variation_type:v}))} options={['addition','omission','substitution'].map(s=>({value:s,label:s}))} />
              <Input label="Submitted Date" type="date" value={form.submitted_date||''} onChange={v=>setForm(f=>({...f,submitted_date:v}))} />
              <Input label="Estimated Cost" type="number" value={form.estimated_cost||''} onChange={v=>setForm(f=>({...f,estimated_cost:v}))} />
              <Input label="Time Impact (days)" type="number" value={form.time_impact_days||''} onChange={v=>setForm(f=>({...f,time_impact_days:parseInt(v)||0}))} />
            </FormGrid>
            <Textarea label="Description" value={form.description||''} onChange={v=>setForm(f=>({...f,description:v}))} rows={2} />
            <div>
              <div style={{fontSize:12,fontWeight:600,color:'var(--text-muted)',marginBottom:8}}>QUANTITY CHANGES</div>
              {form.quantity_variations.map((qv,i)=>(
                <div key={i} style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr',gap:8,marginBottom:8,padding:10,background:'var(--bg-elevated)',borderRadius:8}}>
                  <Select value={qv.boq_item_id||''} onChange={v=>setForm(f=>({...f,quantity_variations:f.quantity_variations.map((q,idx)=>idx===i?{...q,boq_item_id:v}:q)}))}
                    options={[{value:'',label:'— BOQ Item —'},...boq.map(b=>({value:b.boq_item_id,label:`${b.boq_code} – ${(b.description||'').slice(0,25)}`}))]} />
                  <Input placeholder="Orig Qty" type="number" value={qv.original_quantity||''} onChange={v=>setForm(f=>({...f,quantity_variations:f.quantity_variations.map((q,idx)=>idx===i?{...q,original_quantity:v}:q)}))} />
                  <Input placeholder="New Qty"  type="number" value={qv.new_quantity||''} onChange={v=>setForm(f=>({...f,quantity_variations:f.quantity_variations.map((q,idx)=>idx===i?{...q,new_quantity:v}:q)}))} />
                  <Input placeholder="Rate"     type="number" value={qv.rate||''} onChange={v=>setForm(f=>({...f,quantity_variations:f.quantity_variations.map((q,idx)=>idx===i?{...q,rate:v}:q)}))} />
                </div>
              ))}
              <Btn variant="ghost" size="sm" onClick={()=>setForm(f=>({...f,quantity_variations:[...f.quantity_variations,{}]}))}>+ Add Row</Btn>
            </div>
            <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
              <Btn variant="ghost" onClick={()=>setModal(false)}>Cancel</Btn>
              <Btn loading={saving} onClick={async()=>{setSaving(true);try{await api.createVariation({...form,project_id:projectId});toast.show('VO created');setModal(false);load();}catch(e){toast.show(e.message,'error');}setSaving(false);}}>Create VO</Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── QS MEASUREMENTS ─────────────────────────
function QSTab({ projectId, toast }) {
  const [rows, setRows]       = useState([]);
  const [boq, setBOQ]         = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(false);
  const [form, setForm]       = useState({});
  const [saving, setSaving]   = useState(false);
  const [subsections, setSubsections] = useState([]);

  const load = async () => {
    setLoading(true);
    try { 
      const [m, b, subs] = await Promise.all([
        api.listQSMeasurements(projectId), 
        api.listBOQ(projectId),
        api.listDmrSubsections(projectId).catch(() => [])
      ]); 
      setRows(Array.isArray(m)?m:[]); 
      setBOQ(Array.isArray(b)?b:[]); 
      setSubsections(Array.isArray(subs)?subs:[]);
    }
    catch {} setLoading(false);
  };
  useEffect(() => { load(); }, [projectId]);

  const totalCertified = rows.filter(r=>r.status==='certified').reduce((s,r)=>s+parseFloat(r.certified_amount||0),0);

  return (
    <>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
        <div style={{fontSize:13,color:'var(--text-muted)'}}>Total Certified: <strong style={{fontFamily:'var(--mono)',color:'var(--green)'}}>{fmt(totalCertified)}</strong></div>
        <Btn onClick={()=>{setForm({measurement_date:new Date().toISOString().slice(0,10),valuation_number:rows.length+1});setModal(true);}}>Record Measurement</Btn>
      </div>
      <Card>
        <Table loading={loading} cols={[
          {key:'valuation_number',  label:'Val #',       render:v=><span style={{fontFamily:'var(--mono)',color:'var(--cyan)'}}>V{v}</span>},
          {key:'boq_item_id',       label:'BOQ Item',    render:v=>{const b=boq.find(x=>x.boq_item_id===v);return b?<span style={{fontFamily:'var(--mono)',fontSize:11}}>{b.boq_code}</span>:'—';}},
          {key:'measurement_date',  label:'Date',        render:v=>fmtDate(v)},
          {key:'measured_quantity', label:'Measured Qty',render:v=>fmtNum(v)},
          {key:'cumulative_quantity',label:'Cumulative', render:v=>fmtNum(v)},
          {key:'unit_rate',         label:'Rate',        render:v=>fmt(v)},
          {key:'certified_amount',  label:'Certified',   render:v=><span style={{fontFamily:'var(--mono)',fontWeight:700,color:'var(--green)'}}>{fmt(v)}</span>},
          {key:'status',            label:'Status',      render:v=><Badge color={statusColor(v)}>{v}</Badge>},
          {key:'measurement_id',    label:'',            render:id=>(
            <Btn variant="success" size="sm" style={{padding:'3px 8px'}} onClick={async()=>{try{await api.certifyQSMeasurement(id);toast.show('Certified');load();}catch(e){toast.show(e.message,'error');}}}>Certify</Btn>
          )},
        ]} rows={rows} />
      </Card>

      {modal && (
        <Modal title="Record QS Measurement" onClose={()=>setModal(false)}>
          <div style={{display:'flex',flexDirection:'column',gap:14}}>
            <Select label="BOQ Item" required value={form.boq_item_id||''} onChange={v=>setForm(f=>({...f,boq_item_id:v}))}
              options={[{value:'',label:'— Select BOQ Item —'},...boq.map(b=>({value:b.boq_item_id,label:`${b.boq_code} – ${(b.description||'').slice(0,40)}`}))]} />
            <Select label="Sub-section" value={form.dmr_subsection_id||''} onChange={v=>setForm(f=>({...f,dmr_subsection_id:v}))}
              options={[{value:'',label:'— Select Sub-section —'},...subsections.map(s=>({value:s.id,label:s.name}))]} />
            <FormGrid>
              <Input label="Valuation #" type="number" value={form.valuation_number||''} onChange={v=>setForm(f=>({...f,valuation_number:parseInt(v)||1}))} />
              <Input label="Date" type="date" value={form.measurement_date||''} onChange={v=>setForm(f=>({...f,measurement_date:v}))} />
              <Input label="Measured Qty"    type="number" value={form.measured_quantity||''} onChange={v=>setForm(f=>({...f,measured_quantity:v}))} />
              <Input label="Cumulative Qty"  type="number" value={form.cumulative_quantity||''} onChange={v=>setForm(f=>({...f,cumulative_quantity:v}))} />
              <Input label="Unit Rate"       type="number" value={form.unit_rate||''} onChange={v=>setForm(f=>({...f,unit_rate:v}))} />
              <Input label="Certified Amount"type="number" value={form.certified_amount||''} onChange={v=>setForm(f=>({...f,certified_amount:v}))} />
            </FormGrid>
            <Textarea label="Notes" value={form.notes||''} onChange={v=>setForm(f=>({...f,notes:v}))} rows={2} />
            <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
              <Btn variant="ghost" onClick={()=>setModal(false)}>Cancel</Btn>
              <Btn loading={saving} onClick={async()=>{setSaving(true);try{await api.createQSMeasurement({...form,project_id:projectId});toast.show('Measurement recorded');setModal(false);load();}catch(e){toast.show(e.message,'error');}setSaving(false);}}>Save</Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── OTHER COSTS ─────────────────────────────
function OtherCostsTab({ projectId, toast }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(false);
  const [form, setForm]       = useState({});
  const [saving, setSaving]   = useState(false);

  const load = async () => { setLoading(true); try { const d = await api.listOtherCosts(projectId); setRows(Array.isArray(d)?d:[]); } catch {} setLoading(false); };
  useEffect(() => { load(); }, [projectId]);

  return (
    <>
      <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginBottom:14}}>
        <Btn variant="ghost" onClick={()=>exportCSV(rows,'other-costs')}>⬇ CSV</Btn>
        <Btn onClick={()=>{setForm({cost_date:new Date().toISOString().slice(0,10)});setModal(true);}}>+ Add Cost</Btn>
      </div>
      <Card>
        <Table loading={loading} cols={[
          {key:'reference_no',   label:'Ref',      render:v=><span style={{fontFamily:'var(--mono)',fontSize:11,color:'var(--text-muted)'}}>{v||'—'}</span>},
          {key:'category',       label:'Category'},
          {key:'description',    label:'Description'},
          {key:'cost_date',      label:'Date',     render:v=>fmtDate(v)},
          {key:'supplier_vendor',label:'Supplier'},
          {key:'amount',         label:'Amount',   render:v=><span style={{fontFamily:'var(--mono)'}}>{fmt(v)}</span>},
          {key:'vat_amount',     label:'VAT',      render:v=>fmt(v)},
          {key:'net_amount',     label:'Net',      render:v=><span style={{fontFamily:'var(--mono)',fontWeight:700,color:'var(--accent)'}}>{fmt(v)}</span>},
          {key:'cost_id',        label:'',         render:id=>(
            <Btn variant="danger" size="sm" style={{padding:'3px 8px'}} onClick={async()=>{if(window.confirm('Delete?'))try{await api.deleteOtherCost(id);toast.show('Deleted');load();}catch(e){toast.show(e.message,'error');}}}>Del</Btn>
          )},
        ]} rows={rows} />
      </Card>

      {modal && (
        <Modal title="Add Other Cost" onClose={()=>setModal(false)}>
          <div style={{display:'flex',flexDirection:'column',gap:14}}>
            <FormGrid>
              <Input label="Category"       required value={form.category||''} onChange={v=>setForm(f=>({...f,category:v}))} />
              <Input label="Date"           type="date" value={form.cost_date||''} onChange={v=>setForm(f=>({...f,cost_date:v}))} />
              <Input label="Supplier/Vendor" value={form.supplier_vendor||''} onChange={v=>setForm(f=>({...f,supplier_vendor:v}))} />
              <Input label="Reference No"   value={form.reference_no||''} onChange={v=>setForm(f=>({...f,reference_no:v}))} />
              <Input label="Amount"    required type="number" value={form.amount||''} onChange={v=>setForm(f=>({...f,amount:v}))} />
              <Input label="VAT"              type="number" value={form.vat_amount||'0'} onChange={v=>setForm(f=>({...f,vat_amount:v}))} />
              <Input label="WHT"              type="number" value={form.wht_amount||'0'} onChange={v=>setForm(f=>({...f,wht_amount:v}))} />
              <Input label="Net Amount"       type="number" value={form.net_amount||''} onChange={v=>setForm(f=>({...f,net_amount:v}))} />
            </FormGrid>
            <Textarea label="Description" required value={form.description||''} onChange={v=>setForm(f=>({...f,description:v}))} rows={2} />
            <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
              <Btn variant="ghost" onClick={()=>setModal(false)}>Cancel</Btn>
              <Btn loading={saving} onClick={async()=>{setSaving(true);try{await api.createOtherCost({...form,project_id:projectId});toast.show('Cost recorded');setModal(false);load();}catch(e){toast.show(e.message,'error');}setSaving(false);}}>Save</Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── EXPENSES ────────────────────────────────
function ExpensesTab({ projectId, toast }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(false);
  const [form, setForm]       = useState({});
  const [saving, setSaving]   = useState(false);

  const load = async () => { setLoading(true); try { const d = await api.listExpenses(projectId); setRows(Array.isArray(d)?d:[]); } catch {} setLoading(false); };
  useEffect(() => { load(); }, [projectId]);

  return (
    <>
      <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginBottom:14}}>
        <Btn variant="ghost" onClick={()=>exportCSV(rows,'expenses')}>⬇ CSV</Btn>
        <Btn onClick={()=>{setForm({expense_date:new Date().toISOString().slice(0,10)});setModal(true);}}>+ Submit Claim</Btn>
      </div>
      <Card>
        <Table loading={loading} cols={[
          {key:'receipt_reference',label:'Receipt',   render:v=><span style={{fontFamily:'var(--mono)',fontSize:11}}>{v||'—'}</span>},
          {key:'category',         label:'Category'},
          {key:'description',      label:'Description'},
          {key:'expense_date',     label:'Date',      render:v=>fmtDate(v)},
          {key:'amount',           label:'Amount',    render:v=>fmt(v)},
          {key:'net_amount',       label:'Net',       render:v=><span style={{fontFamily:'var(--mono)',fontWeight:700}}>{fmt(v)}</span>},
          {key:'status',           label:'Status',    render:v=><Badge color={statusColor(v)}>{v}</Badge>},
          {key:'expense_id',       label:'',          render:(id,row)=>row.status==='pending'&&(
            <div style={{display:'flex',gap:6}}>
              <Btn variant="success" size="sm" style={{padding:'3px 8px'}} onClick={async()=>{try{await api.updateExpenseStatus(id,{status:'approved'});toast.show('Approved');load();}catch(e){toast.show(e.message,'error');}}}>✓</Btn>
              <Btn variant="danger"  size="sm" style={{padding:'3px 8px'}} onClick={async()=>{const r=window.prompt('Reason?');if(r)try{await api.updateExpenseStatus(id,{status:'rejected',rejection_reason:r});toast.show('Rejected');load();}catch(e){toast.show(e.message,'error');}}}>✕</Btn>
            </div>
          )},
        ]} rows={rows} />
      </Card>

      {modal && (
        <Modal title="Submit Expense Claim" onClose={()=>setModal(false)}>
          <div style={{display:'flex',flexDirection:'column',gap:14}}>
            <FormGrid>
              <Input label="Category"        required value={form.category||''} onChange={v=>setForm(f=>({...f,category:v}))} />
              <Input label="Date"            type="date" value={form.expense_date||''} onChange={v=>setForm(f=>({...f,expense_date:v}))} />
              <Input label="Amount"   required type="number" value={form.amount||''} onChange={v=>setForm(f=>({...f,amount:v}))} />
              <Input label="Net Amount"       type="number" value={form.net_amount||''} onChange={v=>setForm(f=>({...f,net_amount:v}))} />
              <Input label="Receipt Reference" value={form.receipt_reference||''} onChange={v=>setForm(f=>({...f,receipt_reference:v}))} />
            </FormGrid>
            <Textarea label="Description" required value={form.description||''} onChange={v=>setForm(f=>({...f,description:v}))} rows={2} />
            <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
              <Btn variant="ghost" onClick={()=>setModal(false)}>Cancel</Btn>
              <Btn loading={saving} onClick={async()=>{setSaving(true);try{await api.createExpense({...form,project_id:projectId});toast.show('Claim submitted');setModal(false);load();}catch(e){toast.show(e.message,'error');}setSaving(false);}}>Submit</Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── MAIN COMPONENT ──────────────────────────
export default function FinancePage({ toast }) {
  const { projectId, tab } = useParams();

  if (!projectId) return <NeedProject />;
  return (
    <div className="fade-in">
      <SectionHeader title="Finance" subtitle={tab === 'variations' ? 'Variations / Change Orders' : tab === 'qs' ? 'QS Measurements' : tab === 'costs' ? 'Other Costs' : 'Expenses'} />
      {tab==='variations' && <VariationsTab  projectId={projectId} toast={toast} />}
      {tab==='qs'         && <QSTab          projectId={projectId} toast={toast} />}
      {tab==='costs'      && <OtherCostsTab  projectId={projectId} toast={toast} />}
      {tab==='expenses'   && <ExpensesTab    projectId={projectId} toast={toast} />}
    </div>
  );
}
